import React, { useState, useMemo } from "react";
import Select from "react-select";
import { allCountries } from "country-telephone-data";

export const countryList = [
  {
    value: "+91",
    label: "India (+91)",
    dialCode: "+91",
    name: "India",
    iso2: "IN",
  },
  ...allCountries
    .filter((c) => c.dialCode !== "91")
    .map((c) => ({
      value: `+${c.dialCode}`,
      label: `${c.name.split(" (")[0]} (+${c.dialCode})`,
      dialCode: `+${c.dialCode}`,
      name: c.name.split(" (")[0],
      iso2: c.iso2.toUpperCase(),
    }))
    .sort((a, b) => a.name.localeCompare(b.name)),
];

const customFilterOption = (option, inputValue) => {
  if (!inputValue) return true;
  const input = inputValue.toLowerCase().trim().replace(/^\+/, "");
  const data = option.data;
  return (
    data.name.toLowerCase().includes(input) ||
    data.iso2.toLowerCase().includes(input) ||
    data.dialCode.replace("+", "").includes(input) ||
    (data.name.toLowerCase().includes("emirates") && input === "uae") ||
    (data.name.toLowerCase().includes("united states") && (input === "usa" || input === "us")) ||
    (data.name.toLowerCase().includes("united kingdom") && (input === "uk" || input === "gb"))
  );
};

const FlagImg = ({ iso2 }) => {
  const code = (iso2 || "").toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w20/${code}.png`}
      width="20"
      height="14"
      alt={iso2}
      style={{
        borderRadius: "2px",
        objectFit: "cover",
        boxShadow: "0 0 1px rgba(0,0,0,0.3)",
      }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
};


const selectStyles = {
  container: (base) => ({
    ...base,
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
  }),
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    height: "100%",
    minHeight: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    paddingLeft: "0.5rem",
    paddingRight: "0.25rem",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "100%",
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "100%",
    display: "flex",
    alignItems: "center",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "0 4px",
    color: "#6e6e6e",
    "&:hover": {
      color: "#333",
    },
    svg: {
      width: "14px",
      height: "14px",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
    width: "280px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
    borderRadius: "0.75rem",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    marginTop: "6px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "240px",
    padding: "4px 0",
  }),
};

/**
 * Unified Phone Input with seamlessly attached Country Code selector
 */
export const PhoneInputWithCountry = ({
  countryCode = "+91",
  onCountryCodeChange,
  phone = "",
  onPhoneChange,
  placeholder = "Mobile number",
  name = "mobile",
  maxLength = 12,
  required = false,
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const selectedCountry = useMemo(() => {
    return (
      countryList.find(
        (c) => c.dialCode === countryCode || c.value === countryCode
      ) || countryList[0]
    );
  }, [countryCode]);

  return (
    <div
      className={`phone-input-container ${className}`}
      style={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "#fff",
        border: isFocused
          ? "0.0625rem solid var(--primary, #0d6efd)"
          : "0.0625rem solid #d7dae3",
        borderRadius: "1rem",
        height: "3.5rem",
        minHeight: "3.5rem",
        maxHeight: "3.5rem",
        boxShadow: isFocused ? "0 0 0 0.2rem rgba(13, 110, 253, 0.15)" : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        position: "relative",
      }}
    >
      {/* Country Code Select */}
      <div
        style={{
          width: "125px",
          flexShrink: 0,
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Select
          options={countryList}
          value={selectedCountry}
          onChange={(opt) => onCountryCodeChange && onCountryCodeChange(opt?.dialCode || "+91")}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          filterOption={customFilterOption}
          formatOptionLabel={(data, { context }) => {
            if (context === "value") {
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    lineHeight: 1,
                  }}
                >
                  <FlagImg iso2={data.iso2} />
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      color: "#1e293b",
                      lineHeight: 1,
                    }}
                  >
                    {data.dialCode}
                  </span>
                </div>
              );
            }
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "2px 0",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FlagImg iso2={data.iso2} />
                  <span style={{ fontSize: "0.9rem", color: "#333" }}>{data.name}</span>
                </div>
                <span
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "bold",
                    color: "var(--primary, #0d6efd)",
                    marginLeft: "8px",
                  }}
                >
                  {data.dialCode}
                </span>
              </div>
            );
          }}
          styles={selectStyles}
          placeholder="Code"
          isSearchable={true}
          openMenuOnFocus={true}
        />
      </div>

      {/* Vertical Divider */}
      <div
        style={{
          width: "1px",
          height: "50%",
          backgroundColor: "#d7dae3",
          flexShrink: 0,
        }}
      />

      {/* Phone Number Input */}
      <input
        type="tel"
        name={name}
        value={phone}
        onChange={(e) => {
          const val = e.target.value.replace(/[^\d]/g, "");
          onPhoneChange && onPhoneChange(val);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        style={{
          flex: 1,
          height: "100%",
          border: "none",
          outline: "none",
          boxShadow: "none",
          backgroundColor: "transparent",
          padding: "0 1.25rem",
          color: "#333",
          fontSize: "1rem",
          borderRadius: 0,
          display: "flex",
          alignItems: "center",
        }}
      />
    </div>
  );
};

export default PhoneInputWithCountry;
